/**
 * Skin sets in the store and locker: browse a set, try it on any tower, buy one
 * tower's skin or the whole set, equip it per tower or on all your towers.
 * Prices shown here are previews; the server computes the real charge.
 */
import {
  COSMETIC_BY_ID, COVENANTS, COVENANT_BY_ID, RARITY_COLORS, SKIN_SETS, SKIN_SET_BY_ID, TOWERS, TOWER_BY_ID,
  has, ownedPieceCount, ownsCosmetic, skinSetBundleCost, skinSetBundleId, skinSetOfDay, skinSetPieceId, skinSetRecipe, skinSetSavings,
  type Cosmetic, type CovenantId, type SkinSetDef,
} from '@cs/content';
import type { Profile } from '@cs/protocol';
import type { AppContext } from '../../core/app';
import { covenantColor, go, profileOf, today } from '../shell';
import { errorToast, fmt, h, hex, modal, tapSafe, toast } from '../dom';
import { checkIcon, covenantSigil, lockIcon, prismIcon, shardIcon } from '../icons';
import { cosmeticDrawer, previewCanvas, towerDrawer, towerIcon } from '../previews';
import { ownedCovenants } from './covenants';
import { celebrate } from './store';
import '../skinsets.css';

type Cur = 'shards' | 'prisms';
const CURS: Cur[] = ['shards', 'prisms'];

export const setBundle = (s: SkinSetDef) => COSMETIC_BY_ID[skinSetBundleId(s.id)] as Extract<Cosmetic, { slot: 'towerSkin' }>;
export const setPiece = (s: SkinSetDef, towerId: string) => COSMETIC_BY_ID[skinSetPieceId(s.id, towerId)] as Extract<Cosmetic, { slot: 'towerSkin' }> | undefined;

/** Towers of the Covenants you own. */
function myTowers(p: Profile): string[] {
  return ownedCovenants(p).flatMap((c) => COVENANT_BY_ID[c].towers).filter((id) => !!TOWER_BY_ID[id]);
}

/** How much of a set you own, counted over your own towers. */
export function setProgress(p: Profile, s: SkinSetDef): { bundle: boolean; owned: number; total: number } {
  const mine = myTowers(p);
  const bundle = p.inventory.includes(skinSetBundleId(s.id));
  return { bundle, owned: bundle ? mine.length : mine.filter((t) => p.inventory.includes(skinSetPieceId(s.id, t))).length, total: mine.length };
}

const ownsAnyOfSet = (p: Profile, s: SkinSetDef) => { const g = setProgress(p, s); return g.bundle || g.owned > 0; };

function priceRow(price: { shards?: number; prisms?: number }): HTMLElement {
  return h('div', { class: 'price' },
    price.shards !== undefined ? h('span', { class: 'p-shards' }, shardIcon(), fmt(price.shards)) : null,
    price.shards !== undefined && price.prisms !== undefined ? h('span', { class: 'p-or' }, 'or') : null,
    price.prisms !== undefined ? h('span', { class: 'p-prisms' }, prismIcon(), fmt(price.prisms)) : null,
  );
}

const savings = (s: SkinSetDef) => Math.max(...CURS.map((c) => skinSetSavings(s, c)));

/** A set card: cycling preview over several towers, price, savings and your progress. */
export function setCard(ctx: AppContext, p: Profile, s: SkinSetDef, opts: { ribbon?: string; size?: number } = {}): HTMLElement {
  const size = opts.size ?? 168;
  const g = setProgress(p, s);
  const save = savings(s);
  return h('button', {
    class: `card clickable item-card set-card rarity-${s.rarity}${g.bundle ? ' owned' : ''}`, 'data-set': s.id,
    style: { '--rc': hex(RARITY_COLORS[s.rarity]) } as never,
    onclick: () => void openSet(ctx, s.id),
  },
    opts.ribbon ? h('span', { class: 'ic-ribbon' }, opts.ribbon) : null,
    h('div', { class: 'ic-preview' }, previewCanvas(cosmeticDrawer(setBundle(s)), size, size * 0.8)),
    h('div', { class: 'ic-meta' },
      h('div', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), s.rarity),
      h('div', { class: 'ic-name' }, s.name),
      h('div', { class: 'ic-slot' }, `Skin Set · All ${TOWERS.length} towers`),
    ),
    h('div', { class: 'ic-foot' },
      g.bundle ? h('div', { class: 'ic-badge' }, checkIcon(), 'Owned')
        : s.bundleExclusive ? h('div', { class: 'ss-earn' }, 'Battle Pass reward')
        : h('div', { class: 'ss-foot' }, priceRow(s.bundlePrice), save ? h('span', { class: 'ss-save' }, `Save ${save}%`) : null),
      !g.bundle && g.owned ? h('div', { class: 'ss-progress' }, `${g.owned}/${g.total} of your towers`) : null,
    ),
  );
}

/** Store tab: every set. */
export function setShop(ctx: AppContext, p: Profile): HTMLElement {
  const day = skinSetOfDay(today());
  return h('div', { class: 'set-shop' },
    h('div', { class: 'section-head' }, h('h2', null, 'Skin Sets'), h('span', { class: 'muted' }, `${SKIN_SETS.length} sets`)),
    h('p', { class: 'muted ss-intro' }, 'One look for every tower you own, and every tower still to come. Buy a single tower’s skin, or the whole set for much less.'),
    h('div', { class: 'item-grid' }, [...SKIN_SETS].sort((a, b) => Number(b.id === day.id) - Number(a.id === day.id))
      .map((s) => setCard(ctx, p, s, { ribbon: s.id === day.id ? 'Today' : undefined }))),
  );
}

/** Featured hero slide for the set of the day. */
export function setHeroSlide(ctx: AppContext, p: Profile, animate: boolean): HTMLButtonElement {
  const s = skinSetOfDay(today());
  const g = setProgress(p, s);
  const save = savings(s);
  return h('button', { class: `hero-slide set-hero rarity-${s.rarity}`, 'data-set': s.id, style: { '--rc': hex(RARITY_COLORS[s.rarity]) } as never, onclick: () => void openSet(ctx, s.id) },
    h('span', { class: 'hs-glow' }),
    h('span', { class: 'hs-preview' }, previewCanvas(cosmeticDrawer(setBundle(s)), 360, 280, 'preview', animate)),
    h('span', { class: 'hs-info' },
      h('span', { class: 'hs-eyebrow' }, 'Set of the day'),
      h('span', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), s.rarity),
      h('span', { class: 'hs-name' }, s.name),
      h('span', { class: 'hs-slot' }, `Every tower · ${save ? `save ${save}%` : 'full set'}`),
      g.bundle ? h('span', { class: 'ic-badge big' }, checkIcon(), 'Owned') : priceRow(s.bundlePrice),
    ),
  );
}

/** One tower's skin from today's set, for the Daily Offers grid (same for everyone on a UTC day). */
export function dailySetPieceCard(ctx: AppContext, p: Profile): HTMLElement {
  const d = today();
  const s = skinSetOfDay(d);
  const mine = myTowers(p);
  // Prefer a tower you can actually use; rotate daily.
  const pool = mine.length ? mine : TOWERS.map((t) => t.id);
  const towerId = pool[((d * 7) % pool.length + pool.length) % pool.length]!;
  const c = setPiece(s, towerId)!;
  const owned = ownsCosmetic(p.inventory, c.id);
  return h('button', {
    class: `card clickable item-card rarity-${c.rarity}${owned ? ' owned' : ''}`, 'data-set': s.id,
    style: { '--rc': hex(RARITY_COLORS[c.rarity]) } as never, onclick: () => void openSet(ctx, s.id, towerId),
  },
    h('span', { class: 'ic-ribbon' }, 'Daily'),
    h('div', { class: 'ic-preview' }, previewCanvas(towerDrawer(towerId, c.recipe, 2), 168, 134)),
    h('div', { class: 'ic-meta' },
      h('div', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), c.rarity),
      h('div', { class: 'ic-name' }, c.name),
      h('div', { class: 'ic-slot' }, `${s.name} set · ${TOWER_BY_ID[towerId]!.name}`),
    ),
    h('div', { class: 'ic-foot' }, owned ? h('div', { class: 'ic-badge' }, checkIcon(), 'Owned') : priceRow(c.price)),
  );
}

/** Confirm, then buy through the server (which prices and validates everything). */
async function confirmBuy(ctx: AppContext, c: Cosmetic, cur: Cur, price: number, onDone: () => void): Promise<void> {
  const ok = await modal({
    title: `Purchase ${c.name}?`,
    body: h('div', { class: 'confirm-price' }, cur === 'shards' ? shardIcon('ico lg') : prismIcon('ico lg'), h('b', null, fmt(price)), ` ${cur === 'shards' ? 'Shards' : 'Prisms'}`),
    confirm: 'Buy',
    onConfirm: () => ctx.api.buy(c.id, cur),
  });
  if (ok) { ctx.audio.ui('purchase'); onDone(); celebrate(c); }
}

/** The set sheet: try-on preview on any tower, buy a tower or the set, equip. */
export async function openSet(ctx: AppContext, setId: string, towerId?: string): Promise<void> {
  if (!has(SKIN_SET_BY_ID, setId)) return;
  const s = SKIN_SET_BY_ID[setId]!;
  let p = profileOf(ctx);
  const startTower = towerId && TOWER_BY_ID[towerId] ? towerId : undefined;
  const active = p.loadouts[p.activeLoadout]?.covenant;
  let cov: CovenantId = (startTower && TOWER_BY_ID[startTower]!.covenant) || (active && COVENANT_BY_ID[active] ? active : ownedCovenants(p)[0] ?? COVENANTS[0]!.id);
  let tower = startTower ?? COVENANT_BY_ID[cov].towers[0]!;
  let tryOn = true;
  let close: ((ok: boolean) => void) | undefined;

  const big = Math.min(380, Math.round(innerWidth * 0.84));
  const stage = h('div', { class: 'id-stage' });
  const paintStage = () => stage.replaceChildren(previewCanvas(towerDrawer(tower, tryOn ? skinSetRecipe(s.id, tower) : undefined, 2), big, Math.round(big * 0.74)));
  const toggle = h('button', { class: 'tryon on', 'data-sfx': 'click', 'aria-pressed': 'true', onclick: () => {
    tryOn = !tryOn;
    toggle.classList.toggle('on', tryOn);
    toggle.setAttribute('aria-pressed', String(tryOn));
    toggle.lastChild!.textContent = tryOn ? 'Wearing it' : 'Without it';
    paintStage();
  } }, h('span', { class: 'tryon-knob' }), h('span', null, 'Wearing it'));

  const covRow = h('div', { class: 'ss-covs', role: 'tablist', 'aria-label': 'Covenant' });
  const towerRow = h('div', { class: 'ss-towers', role: 'tablist', 'aria-label': 'Tower' });
  const towerName = h('div', { class: 'ss-tower-name' });
  const actions = h('div', { class: 'ss-actions' });

  const paintCovs = () => {
    const mine = new Set(ownedCovenants(p));
    covRow.replaceChildren(...COVENANTS.map((c) => {
      const col = covenantColor(c.id);
      return h('button', {
        class: `ss-cov${c.id === cov ? ' active' : ''}${mine.has(c.id) ? '' : ' locked'}`, role: 'tab', 'aria-selected': String(c.id === cov), 'aria-label': c.name, title: c.name,
        onclick: () => { if (cov === c.id) return; cov = c.id; tower = COVENANT_BY_ID[cov].towers[0]!; paintCovs(); paintTowers(); paintStage(); renderActions(); },
      }, covenantSigil(c.id, col.primary, col.glow, 'sigil xs'), mine.has(c.id) ? null : lockIcon());
    }));
  };
  const paintTowers = () => {
    towerRow.replaceChildren(...COVENANT_BY_ID[cov].towers.filter((id) => TOWER_BY_ID[id]).map((id) => h('button', {
      class: `ss-tower${id === tower ? ' active' : ''}${ownsCosmetic(p.inventory, skinSetPieceId(s.id, id)) ? ' owned' : ''}`, role: 'tab', 'aria-selected': String(id === tower), 'aria-label': TOWER_BY_ID[id]!.name, title: TOWER_BY_ID[id]!.name,
      onclick: () => { if (tower === id) return; tower = id; paintTowers(); paintStage(); renderActions(); },
    }, towerIcon(id, 44, skinSetRecipe(s.id, id)))));
    towerName.textContent = TOWER_BY_ID[tower]!.name;
  };

  const buyButtons = (c: Cosmetic, priceOf: (cur: Cur) => number | undefined, list?: (cur: Cur) => number | undefined) => h('div', { class: 'id-buy' }, CURS.map((cur) => {
    const price = priceOf(cur);
    if (price === undefined) return null;
    const bal = cur === 'shards' ? p.shards : p.prisms;
    const was = list?.(cur);
    return h('button', {
      class: `btn big buy-btn ${cur === 'prisms' ? 'arcane' : 'primary'}`, disabled: bal < price, 'data-sfx': 'confirm',
      onclick: () => void confirmBuy(ctx, c, cur, price, () => undefined),
    }, cur === 'shards' ? shardIcon() : prismIcon(), bal >= price ? fmt(price) : `Need ${fmt(price - bal)}`,
      was !== undefined && was > price ? h('s', { class: 'ss-was' }, fmt(was)) : null);
  }));

  const renderActions = () => {
    const piece = setPiece(s, tower)!;
    const pieceOwned = ownsCosmetic(p.inventory, piece.id);
    const usable = ownedCovenants(p).includes(TOWER_BY_ID[tower]!.covenant as CovenantId);
    const eq = p.equipped.towerSkins[tower] === piece.id;
    const g = setProgress(p, s);
    const credit = ownedPieceCount(p.inventory, s.id);
    const bundle = setBundle(s);
    const save = savings(s);

    const towerBlock = h('div', { class: 'ss-block' },
      h('div', { class: 'ss-block-head' }, h('b', null, TOWER_BY_ID[tower]!.name), h('span', { class: 'muted' }, 'this tower')),
      pieceOwned
        ? (eq ? h('div', { class: 'ic-badge eq big' }, checkIcon(), 'Equipped')
          : usable ? h('button', { class: 'btn primary big', 'data-sfx': 'equip', onclick: async () => {
            try { await ctx.api.equip('towerSkin', piece.id, tower); ctx.audio.ui('equip'); toast(`${piece.name} equipped`, 'good'); } catch (e) { errorToast(e); }
          } }, 'Equip on this tower')
          : h('div', { class: 'muted small' }, `Owned. Unlock ${COVENANT_BY_ID[TOWER_BY_ID[tower]!.covenant as CovenantId]?.name ?? 'its Covenant'} to use it.`))
        : buyButtons(piece, (cur) => piece.price[cur]),
    );

    const setBlock = h('div', { class: 'ss-block' },
      h('div', { class: 'ss-block-head' }, h('b', null, 'Whole set'), h('span', { class: 'muted' }, g.bundle ? 'every tower' : save ? `every tower · save ${save}%` : 'every tower')),
      g.bundle ? h('div', { class: 'ic-badge big' }, checkIcon(), 'You own the whole set')
        : s.bundleExclusive ? h('div', { class: 'ss-earn-row' }, h('span', { class: 'muted' }, s.earn ?? 'Earned, not sold.'), h('button', { class: 'btn ghost', onclick: () => { close?.(false); go(ctx, 'battlepass'); } }, 'Battle Pass'))
        : h('div', null,
          buyButtons(bundle, (cur) => skinSetBundleCost(s, cur, credit), (cur) => s.bundlePrice[cur]),
          credit ? h('div', { class: 'muted small' }, `Price reduced for the ${credit} tower skin${credit === 1 ? '' : 's'} you already own.`) : null),
    );

    actions.replaceChildren(...([
      towerBlock,
      setBlock,
      ownsAnyOfSet(p, s) ? h('button', { class: 'btn big ss-equip-all', 'data-sfx': 'equip', onclick: async () => {
        try { await ctx.api.equipSet(s.id); ctx.audio.ui('equip'); toast(`${s.name} equipped on all your towers`, 'good'); } catch (e) { errorToast(e); }
      } }, 'Equip set on all my towers') : null,
      g.owned && !g.bundle ? h('div', { class: 'ss-progress' }, `You own ${g.owned} of ${g.total} for your towers`) : null,
    ].filter(Boolean) as Node[]));
  };

  paintCovs(); paintTowers(); paintStage(); renderActions();
  const off = ctx.api.subscribe(tapSafe((np: Profile) => { p = np; paintCovs(); paintTowers(); renderActions(); }));
  try {
    await modal({
      title: `${s.name} Set`,
      wide: true,
      cancel: 'Close',
      onOpen: (c) => { close = c; },
      body: h('div', { class: `item-detail set-sheet rarity-${s.rarity}`, style: { '--rc': hex(RARITY_COLORS[s.rarity]) } as never },
        h('div', { class: 'id-preview' }, stage, toggle),
        h('div', { class: 'id-info' },
          h('div', { class: 'ic-rarity' }, h('span', { class: 'rarity-gem' }), s.rarity),
          h('p', { class: 'id-desc' }, s.desc),
          covRow, towerRow, towerName,
          actions,
        ),
      ),
    });
  } finally { off(); }
}
